import React, { useState, useEffect, useRef } from 'react';
import { Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronRight, Eye, EyeOff, GripVertical, Upload, Play, X } from 'lucide-react';
import { API_URL } from '../../config';

export default function VideoTemplatesAdmin() {
  const [templates, setTemplates] = useState({ motion: [], trend: [], camera: [], global_prompt: '', base_motion: '', talking_lipsync_prompt: '', models: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('motion');
  const [editingItem, setEditingItem] = useState(null);
  const [message, setMessage] = useState(null);
  const [uploadingChip, setUploadingChip] = useState(null); // {category, chipId}
  const fileInputRef = useRef(null);
  
  // Section Titles
  const [sectionTitles, setSectionTitles] = useState({
    motion: 'Bewegung',
    camera: '🎥 Kamerafahrt',
    trends: '🌟 Trendeffekte',
    models: 'KI-Modell'
  });
  const [savingTitles, setSavingTitles] = useState(false);

  // Prompt Settings (Beschreibungsfeld)
  const [promptSettings, setPromptSettings] = useState({
    label: 'Beschreibung',
    placeholder: 'Was soll im Video passieren? z.B. lächelt und winkt in die Kamera'
  });
  const [savingPrompt, setSavingPrompt] = useState(false);

  // Load templates + Section Titles + Prompt Settings
  useEffect(() => {
    loadTemplates();
    loadSectionTitles();
    loadPromptSettings();
  }, []);
  
  const loadSectionTitles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/site-settings/section-titles`);
      if (response.ok) {
        const data = await response.json();
        setSectionTitles(data);
      }
    } catch (error) {
      console.error('Error loading section titles:', error);
    }
  };
  
  const saveSectionTitles = async () => {
    setSavingTitles(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/site-settings/section-titles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sectionTitles)
      });
      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Überschriften gespeichert!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Fehler beim Speichern der Überschriften' });
    } finally {
      setSavingTitles(false);
    }
  };

  const loadPromptSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/site-settings/prompt-settings`);
      if (response.ok) {
        const data = await response.json();
        setPromptSettings(data);
      }
    } catch (error) {
      console.error('Error loading prompt settings:', error);
    }
  };

  const savePromptSettings = async () => {
    setSavingPrompt(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/site-settings/prompt-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(promptSettings)
      });
      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Beschreibungsfeld-Texte gespeichert!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Fehler beim Speichern' });
    } finally {
      setSavingPrompt(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/video-templates/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden' });
    } finally {
      setLoading(false);
    }
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/video-templates/admin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templates)
      });
      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Gespeichert!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Fehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Alle Templates auf Defaults zurücksetzen?')) return;
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/video-templates/admin/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
        setMessage({ type: 'success', text: '🔄 Auf Defaults zurückgesetzt' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Fehler beim Reset' });
    }
  };

  const updateTemplate = (category, index, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [category]: prev[category].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const toggleEnabled = (category, index) => {
    updateTemplate(category, index, 'enabled', !templates[category][index].enabled);
  };

  const addTemplate = (category) => {
    const newTemplate = {
      id: `new_${Date.now()}`,
      label: 'Neuer Effekt',
      prompt: 'Beschreibe den Effekt hier...',
      enabled: true,
      order: templates[category].length
    };
    setTemplates(prev => ({
      ...prev,
      [category]: [...prev[category], newTemplate]
    }));
    setEditingItem({ category, index: templates[category].length });
  };

  const deleteTemplate = (category, index) => {
    if (!window.confirm('Template wirklich löschen?')) return;
    setTemplates(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const moveTemplate = (category, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= templates[category].length) return;
    
    setTemplates(prev => {
      const arr = [...prev[category]];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      // Update order values
      arr.forEach((item, i) => item.order = i);
      return { ...prev, [category]: arr };
    });
  };

  // Video Preview Upload
  const handleVideoUpload = async (e, category, chipId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setMessage({ type: 'error', text: '❌ Bitte nur Video-Dateien hochladen' });
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ Video zu groß (max. 50MB)' });
      return;
    }

    setUploadingChip({ category, chipId });
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('chip_id', chipId);
      formData.append('category', category);

      const response = await fetch(`${API_URL}/api/admin/chip-preview/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update local state
        setTemplates(prev => ({
          ...prev,
          [category]: prev[category].map(chip => 
            chip.id === chipId 
              ? { ...chip, preview_video_url: data.preview_url }
              : chip
          )
        }));
        setMessage({ type: 'success', text: `✅ Preview für "${chipId}" hochgeladen!` });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error(data.detail || 'Upload fehlgeschlagen');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: `❌ Upload fehlgeschlagen: ${error.message}` });
    } finally {
      setUploadingChip(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deletePreviewVideo = async (category, chipId) => {
    if (!window.confirm('Preview-Video wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${API_URL}/api/admin/chip-preview/${category}/${chipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Update local state
        setTemplates(prev => ({
          ...prev,
          [category]: prev[category].map(chip => 
            chip.id === chipId 
              ? { ...chip, preview_video_url: null }
              : chip
          )
        }));
        setMessage({ type: 'success', text: '🗑️ Preview gelöscht' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Löschen fehlgeschlagen' });
    }
  };

  const categoryLabels = {
    motion: '🎭 Bewegung (Motion)',
    trend: '✨ Trend Effekte',
    camera: '🎥 Kamerafahrten'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">🎬 Video-Templates</h2>
        <div className="flex gap-2">
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
          >
            <RotateCcw size={16} />
            Reset
          </button>
          <button
            onClick={saveTemplates}
            disabled={saving}
            className="px-4 py-2 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* ═══ SECTION TITLES ═══ */}
      <div className="bg-[#1a1a2e] rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🏷️</span>
            <h3 className="font-medium text-white">Sektion-Überschriften</h3>
            <span className="text-xs text-gray-400">(VideoGenerator UI)</span>
          </div>
          <button
            onClick={saveSectionTitles}
            disabled={savingTitles}
            className="px-3 py-1.5 bg-[#10B981] hover:bg-[#059669] text-white text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"
          >
            <Save size={14} />
            {savingTitles ? '...' : 'Speichern'}
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Bewegung</label>
            <input
              type="text"
              value={sectionTitles.motion}
              onChange={(e) => setSectionTitles(prev => ({ ...prev, motion: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Kamerafahrt</label>
            <input
              type="text"
              value={sectionTitles.camera}
              onChange={(e) => setSectionTitles(prev => ({ ...prev, camera: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Trendeffekte</label>
            <input
              type="text"
              value={sectionTitles.trends}
              onChange={(e) => setSectionTitles(prev => ({ ...prev, trends: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">KI-Modell</label>
            <input
              type="text"
              value={sectionTitles.models}
              onChange={(e) => setSectionTitles(prev => ({ ...prev, models: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* ═══ PROMPT/BESCHREIBUNGSFELD SETTINGS ═══ */}
      <div className="bg-[#1a1a2e] rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">✏️</span>
            <h3 className="font-medium text-white">Beschreibungsfeld</h3>
            <span className="text-xs text-gray-400">(User-Eingabefeld im VideoGenerator)</span>
          </div>
          <button
            onClick={savePromptSettings}
            disabled={savingPrompt}
            className="px-3 py-1.5 bg-[#10B981] hover:bg-[#059669] text-white text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"
          >
            <Save size={14} />
            {savingPrompt ? '...' : 'Speichern'}
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Überschrift über dem Eingabefeld</label>
            <input
              type="text"
              value={promptSettings.label}
              onChange={(e) => setPromptSettings(prev => ({ ...prev, label: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
              placeholder="z.B. Beschreibung"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Placeholder-Text im Eingabefeld</label>
            <textarea
              value={promptSettings.placeholder}
              onChange={(e) => setPromptSettings(prev => ({ ...prev, placeholder: e.target.value }))}
              className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm resize-y min-h-[60px]"
              placeholder="z.B. Was soll im Video passieren?"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Globale Prompts - werden IMMER mitgeschickt */}
      <div className="bg-[#1a1a2e] rounded-xl p-4 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🛡️</span>
            <h3 className="font-medium text-white">Face Protection</h3>
            <span className="text-xs text-gray-400">(Gesichtsschutz - IMMER am Ende)</span>
          </div>
          <textarea
            value={templates.global_prompt || ''}
            onChange={(e) => setTemplates(prev => ({ ...prev, global_prompt: e.target.value }))}
            className="w-full px-4 py-3 bg-black/30 border border-[#10B981]/30 rounded-lg text-white resize-y min-h-[60px]"
            placeholder="z.B. preserve exact facial features, same person, no face morphing..."
            rows={2}
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🎬</span>
            <h3 className="font-medium text-white">Base Motion</h3>
            <span className="text-xs text-gray-400">(Basis-Bewegung - IMMER am Ende)</span>
          </div>
          <textarea
            value={templates.base_motion || ''}
            onChange={(e) => setTemplates(prev => ({ ...prev, base_motion: e.target.value }))}
            className="w-full px-4 py-3 bg-black/30 border border-[#10B981]/30 rounded-lg text-white resize-y min-h-[60px]"
            placeholder="z.B. minimal movement, maintain facial features..."
            rows={2}
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🎤</span>
            <h3 className="font-medium text-white">Talking/Lipsync Prompt</h3>
            <span className="text-xs text-gray-400">(wird hinzugefügt wenn 🎤 Talking-Chip aktiv)</span>
          </div>
          <textarea
            value={templates.talking_lipsync_prompt || ''}
            onChange={(e) => setTemplates(prev => ({ ...prev, talking_lipsync_prompt: e.target.value }))}
            className="w-full px-4 py-3 bg-black/30 border border-[#10B981]/30 rounded-lg text-white resize-y min-h-[80px]"
            placeholder="z.B. PERSON SPEAKING DIRECTLY TO CAMERA WITH CLEAR LIP SYNC, mouth moving naturally..."
            rows={3}
          />
        </div>
      </div>

      {/* Video-Modelle */}
      <div className="bg-[#1a1a2e] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">🎥</span>
          <h3 className="font-medium text-white">Video-Modelle</h3>
          <span className="text-xs text-gray-400">(Preise & Labels)</span>
        </div>
        <div className="space-y-3">
          {Object.entries(templates.models || {}).map(([key, model]) => (
            <div key={key} className={`p-3 rounded-lg border ${model.enabled !== false ? 'bg-[#252540] border-[#10B981]/30' : 'bg-[#1a1a2e] border-gray-700 opacity-60'}`}>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Enable Toggle */}
                <button
                  onClick={() => setTemplates(prev => ({
                    ...prev,
                    models: {
                      ...prev.models,
                      [key]: { ...prev.models[key], enabled: !prev.models[key]?.enabled }
                    }
                  }))}
                  className={`w-8 h-8 rounded flex items-center justify-center ${model.enabled !== false ? 'bg-[#10B981] text-white' : 'bg-gray-700 text-gray-400'}`}
                >
                  {model.enabled !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                
                {/* Key (readonly) */}
                <span className="text-xs text-gray-500 font-mono w-28">{key}</span>
                
                {/* Label */}
                <input
                  type="text"
                  value={model.label || ''}
                  onChange={(e) => setTemplates(prev => ({
                    ...prev,
                    models: {
                      ...prev.models,
                      [key]: { ...prev.models[key], label: e.target.value }
                    }
                  }))}
                  className="flex-1 min-w-[120px] px-2 py-1 bg-black/30 border border-gray-700 rounded text-white"
                  placeholder="Label"
                />
                
                {/* Price */}
                <input
                  type="text"
                  value={model.price || ''}
                  onChange={(e) => setTemplates(prev => ({
                    ...prev,
                    models: {
                      ...prev.models,
                      [key]: { ...prev.models[key], price: e.target.value }
                    }
                  }))}
                  className="w-24 px-2 py-1 bg-black/30 border border-gray-700 rounded text-[#10B981] font-medium"
                  placeholder="Preis"
                />
                
                {/* Description */}
                <input
                  type="text"
                  value={model.description || ''}
                  onChange={(e) => setTemplates(prev => ({
                    ...prev,
                    models: {
                      ...prev.models,
                      [key]: { ...prev.models[key], description: e.target.value }
                    }
                  }))}
                  className="flex-1 min-w-[150px] px-2 py-1 bg-black/30 border border-gray-700 rounded text-gray-300 text-sm"
                  placeholder="Beschreibung"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      {Object.entries(categoryLabels).map(([category, label]) => (
        <div key={category} className="bg-[#1a1a2e] rounded-xl overflow-hidden">
          {/* Category Header */}
          <button
            onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
            className="w-full px-4 py-3 flex items-center justify-between bg-[#252540] hover:bg-[#2a2a4a]"
          >
            <div className="flex items-center gap-2">
              {expandedCategory === category ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-medium text-white">{label}</span>
              <span className="text-sm text-gray-400">({templates[category]?.length || 0})</span>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); addTemplate(category); }}
              className="p-1 hover:bg-[#10B981]/20 rounded"
            >
              <Plus size={18} className="text-[#10B981]" />
            </button>
          </button>

          {/* Templates List */}
          {expandedCategory === category && (
            <div className="p-4 space-y-2">
              {templates[category]?.map((template, index) => (
                <div
                  key={template.id}
                  className={`p-3 rounded-lg border ${template.enabled ? 'bg-[#252540] border-[#10B981]/30' : 'bg-[#1a1a2e] border-gray-700 opacity-60'}`}
                >
                  {/* Template Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical size={16} className="text-gray-500 cursor-move" />
                    
                    {/* Move buttons */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveTemplate(category, index, -1)}
                        disabled={index === 0}
                        className="text-xs text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => moveTemplate(category, index, 1)}
                        disabled={index === templates[category].length - 1}
                        className="text-xs text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        ▼
                      </button>
                    </div>

                    {/* ID */}
                    <input
                      type="text"
                      value={template.id}
                      onChange={(e) => updateTemplate(category, index, 'id', e.target.value)}
                      className="w-32 px-2 py-1 bg-black/30 border border-gray-700 rounded text-sm text-gray-300 font-mono"
                      placeholder="id"
                    />

                    {/* Label */}
                    <input
                      type="text"
                      value={template.label}
                      onChange={(e) => updateTemplate(category, index, 'label', e.target.value)}
                      className="flex-1 px-2 py-1 bg-black/30 border border-gray-700 rounded text-white"
                      placeholder="Label"
                    />

                    {/* Toggle enabled */}
                    <button
                      onClick={() => toggleEnabled(category, index)}
                      className={`p-1.5 rounded ${template.enabled ? 'text-[#10B981]' : 'text-gray-500'}`}
                    >
                      {template.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => deleteTemplate(category, index)}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Prompt textarea */}
                  <textarea
                    value={template.prompt}
                    onChange={(e) => updateTemplate(category, index, 'prompt', e.target.value)}
                    className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-sm text-gray-300 resize-y min-h-[60px]"
                    placeholder="Prompt für Video-Generierung..."
                    rows={2}
                  />

                  {/* Preview Video Section */}
                  <div className="mt-3 p-2 bg-black/20 rounded-lg border border-gray-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Play size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-400">Preview-Video</span>
                    </div>
                    
                    {template.preview_video_url ? (
                      // Video vorhanden - Preview anzeigen
                      <div className="flex items-center gap-3">
                        <video 
                          src={template.preview_video_url}
                          className="w-24 h-24 object-cover rounded-lg bg-black"
                          muted
                          loop
                          playsInline
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        />
                        <div className="flex-1">
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">
                            {template.preview_video_url.split('/').pop()}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={(e) => handleVideoUpload(e, category, template.id)}
                                disabled={uploadingChip?.chipId === template.id}
                              />
                              <span className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded flex items-center gap-1">
                                <Upload size={12} />
                                Ersetzen
                              </span>
                            </label>
                            <button
                              onClick={() => deletePreviewVideo(category, template.id)}
                              className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded flex items-center gap-1"
                            >
                              <X size={12} />
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Kein Video - Upload Button
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          ref={fileInputRef}
                          onChange={(e) => handleVideoUpload(e, category, template.id)}
                          disabled={uploadingChip?.chipId === template.id}
                        />
                        <div className={`
                          flex items-center justify-center gap-2 p-3 
                          border-2 border-dashed border-gray-600 hover:border-[#10B981] 
                          rounded-lg transition-colors
                          ${uploadingChip?.chipId === template.id ? 'opacity-50' : ''}
                        `}>
                          {uploadingChip?.chipId === template.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#10B981]"></div>
                              <span className="text-xs text-gray-400">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload size={16} className="text-gray-400" />
                              <span className="text-xs text-gray-400">Preview-Video hochladen (max. 50MB)</span>
                            </>
                          )}
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              ))}

              {templates[category]?.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Keine Templates. Klicke + um eins hinzuzufügen.
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Stats */}
      <div className="text-sm text-gray-500 text-center">
        Gesamt: {(templates.motion?.length || 0) + (templates.trend?.length || 0) + (templates.camera?.length || 0)} Templates
        ({templates.motion?.filter(t => t.enabled).length || 0} Motion, 
        {templates.trend?.filter(t => t.enabled).length || 0} Trend, 
        {templates.camera?.filter(t => t.enabled).length || 0} Kamera aktiv)
      </div>
    </div>
  );
}
